import Axios, { AxiosError } from 'axios';

import { TABLET_API_ORIGIN } from '@/global/env';
import { errorHandler } from '@/lib/ErrorHandler';
import { handleAxiosError, RestClientInterface } from '@/lib/restClient';
import {
  KioskAccount,
  StoreChangePasswordParams,
  StoreCreateParams,
  StoreDeleteParams,
  StoreListParams,
  StoreListResponse,
  StoreMainImagePresignedUrlParams,
  StoreMainImagePresignedUrlRequest,
  StoreMainImagePresignedUrlResponse,
  StoreSettingUpdateParams,
  StoreSettingUpdateRequest,
} from '@/services/Kiosk/type';

export class KioskService {
  constructor(private readonly restClient: RestClientInterface) {}

  public async storeList(params: StoreListParams): Promise<StoreListResponse> {
    return await this.restClient
      .get<StoreListResponse>(
        `${TABLET_API_ORIGIN}/dashboard-api/kiosk/master-stores?limit=${
          params.limit
        }&current_page=${params.currentPage}&name=${params.name || ''}` as const
      )
      .catch((error: Error) => errorHandler({ error }));
  }

  public async updateStoreSetting(
    params: StoreSettingUpdateParams
  ): Promise<unknown> {
    return await this.restClient
      .patch<StoreSettingUpdateRequest, unknown>(
        `${TABLET_API_ORIGIN}/dashboard-api/kiosk/store_settings/${params.deliveryStoreId}` as const,
        {
          primary_color_code: params.primaryColorCode,
          privacy_policy_url: params.privacyPolicyUrl,
          store_main_image_url: params.storeMainImageUrl,
        }
      )
      .catch((error: Error) => errorHandler({ error }));
  }

  public async createStore(params: StoreCreateParams): Promise<unknown> {
    return await this.restClient
      .post(`${TABLET_API_ORIGIN}/dashboard-api/kiosk/accounts` as const, {
        master_store_id: Number.parseInt(params.masterStoreId, 10),
        password: params.password,
      })
      .catch((error: Error) => errorHandler({ error }));
  }

  public async changePassword(
    params: StoreChangePasswordParams
  ): Promise<unknown> {
    return await this.restClient
      .patch(
        `${TABLET_API_ORIGIN}/dashboard-api/kiosk/accounts/${params.accountId}` as const,
        params
      )
      .catch((error: Error) => errorHandler({ error }));
  }

  public async deleteStore(params: StoreDeleteParams): Promise<unknown> {
    return await this.restClient
      .delete(
        `${TABLET_API_ORIGIN}/dashboard-api/kiosk/accounts/${params.accountId}` as const
      )
      .catch((error: Error) => errorHandler({ error }));
  }

  public async createStoreMainImagePresignedUrl(
    params: StoreMainImagePresignedUrlParams
  ): Promise<StoreMainImagePresignedUrlResponse> {
    return await this.restClient
      .post<
        StoreMainImagePresignedUrlRequest,
        StoreMainImagePresignedUrlResponse
      >(
        `${TABLET_API_ORIGIN}/dashboard-api/kiosk/store-main-images/presigned-url` as const,
        {
          content_type: params.contentType,
          file_ext: params.fileExt,
        }
      )
      .catch((error) => errorHandler({ error }));
  }

  public async putFile(url: string, file: File): Promise<void> {
    await Axios.put(url, file, {
      headers: { 'Content-Type': file.type },
    })
      .catch((error: AxiosError) => {
        handleAxiosError(error);
      })
      .catch((error) => errorHandler({ error }));
  }

  public async getAccount(accountId: string): Promise<KioskAccount> {
    return await this.restClient
      .get<KioskAccount>(
        `${TABLET_API_ORIGIN}/dashboard-api/kiosk/accounts/${accountId}` as const
      )
      .catch((error) => errorHandler({ error }));
  }
}
